import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import UnsavedChangesWarning from '../../common/UnsavedChangesWarning';
import { FLASHCARD_INPUT_MAX_LENGTH } from '../../common/constants';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MssModal from '../../common/MssModal';
import Loader from '../../common/Loader';
import EditDeckHeader from './EditDeckHeader';
import EditDeckBody from './EditDeckBody';
import AIResponseError from '../../common/AIResponseError';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import useUnsavedChangesGuard from '../../common/useUnsavedChangesGuard';
import PageBreadcrumbs from '../../common/Breadcrumbs/PageBreadcrumbs';
import { TruncatedTitle } from '../../common/TruncatedTitle';
import { patchData } from '../../../DataAccessLayer';
import { flashcards } from '../../../DataAccessLayer/services';

function EditDeck() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const courseId = useSelector((state) => state.course.courseId);
  const showBanner = isImpersonating || !!courseId;
  const [isSaving, setIsSaving] = useState(false);
  // When the editor last saved, which it reports in place of the warning.
  const [savedAt, setSavedAt] = useState(null);

  const { deck, setDeck, isLoading, loadError } = useOutletContext();

  const [title, setTitle] = useState(deck?.title ?? '');
  const [cards, setCards] = useState(deck?.cards ?? []);
  const hasSeeded = useRef(!!deck);

  useEffect(() => {
    if (deck && !hasSeeded.current) {
      hasSeeded.current = true;
      setTitle(deck.title);
      setCards(deck.cards ?? []);
    }
  }, [deck]);

  const originalCardsById = new Map((deck?.cards ?? []).map((card) => [card.id, card]));

  const isCardNew = (card) => !card.id;

  const isCardEdited = (card) => {
    if (!card.id) return false;
    const original = originalCardsById.get(card.id);
    if (!original) return false;
    return (
      card.name !== original.name ||
      card.definition !== original.definition ||
      card.hint !== original.hint
    );
  };

  const isDirty =
    !!deck &&
    hasSeeded.current &&
    (title !== deck.title ||
      cards.length !== (deck.cards ?? []).length ||
      cards.some((card) => isCardNew(card) || isCardEdited(card)));

  const updateCardField = (cardIndex, field, value) => {
    setCards((prev) =>
      prev.map((card, position) => (position === cardIndex ? { ...card, [field]: value } : card)),
    );
  };

  const removeCard = (cardIndex) => {
    setCards((prev) => prev.filter((_, position) => position !== cardIndex));
  };

  const addCardBelow = (cardIndex) => {
    setCards((prev) => [
      ...prev.slice(0, cardIndex + 1),
      { name: '', definition: '', hint: '' },
      ...prev.slice(cardIndex + 1),
    ]);
  };

  // Edits live in the page until Save is pressed, so leaving throws them away.
  const { isPromptOpen, guardNavigation, navigateAway, stay, leave } = useUnsavedChangesGuard({
    isDirty,
    backPath: '..',
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedDeck = await patchData(flashcards({ id: deckId, courseId }), {
        title: title.trim(),
        cards: cards.map(({ id, name, definition, hint }) => ({
          id,
          name: name.trim(),
          definition: definition.trim(),
          hint: hint ?? '',
        })),
      });
      setDeck(updatedDeck);
      /*
       * The editor stays where it is, so the fields take the shape of what was
       * actually saved: a name comes back trimmed, and cards written in this
       * edit come back with ids. Left as they were typed, the page would go on
       * reading as unsaved against a deck it matches.
       */
      setTitle(updatedDeck.title ?? '');
      setCards(updatedDeck.cards ?? []);
      toast.success('Deck updated.');
      setSavedAt(new Date());
    } catch (err) {
      console.log(err);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const hasMaxLengthError = cards.some(
    (card) =>
      card.name.length > FLASHCARD_INPUT_MAX_LENGTH.NAME ||
      card.definition.length > FLASHCARD_INPUT_MAX_LENGTH.DEFINITION ||
      (card.hint ?? '').length > FLASHCARD_INPUT_MAX_LENGTH.HINT,
  );

  const isSaveDisabled =
    isSaving ||
    !isDirty ||
    !title.trim() ||
    cards.length === 0 ||
    cards.some((card) => !card.name.trim() || !card.definition.trim()) ||
    hasMaxLengthError;

  return (
    <Box className={'mss__editDeck' + (showBanner ? ' impersonating' : '')} id="editDeck">
      <PageBreadcrumbs
        id="editDeck__nav_breadcrumbs"
        rootTo="../../.."
        isVisible={!loadError}
        onRootClick={(event) => {
          event.preventDefault();
          guardNavigation('../../..');
        }}
      >
        <BreadcrumbCrumb
          isLoading={isLoading}
          to=".."
          width={100}
          onClick={(event) => {
            event.preventDefault();
            guardNavigation('..');
          }}
        >
          <TruncatedTitle name={deck?.title} isFocusable={false} variant="inherit" />
        </BreadcrumbCrumb>
        <BreadcrumbCrumb isLoading={isLoading} width={140}>
          {deck && (
            <>
              Edit <TruncatedTitle name={deck.title} variant="inherit" />
            </>
          )}
        </BreadcrumbCrumb>
      </PageBreadcrumbs>

      {loadError && (
        <AIResponseError
          message={loadError}
          onGoToDashboard={() => navigate('../../..', { relative: 'path' })}
        />
      )}

      {!loadError && (
        <>
          <EditDeckHeader
            deck={deck ?? {}}
            isLoading={isLoading}
            isDirty={isDirty}
            isSaveDisabled={isSaveDisabled}
            onSave={handleSave}
            savedAt={savedAt}
            onDeleted={() => navigateAway('../../..')}
          />

          <UnsavedChangesWarning
            isDirty={isDirty}
            savedAt={savedAt}
            className="unsavedWarningMobile"
          />

          <Box className="mss__box editDeckContent" id="editDeck__box_content">
            <EditDeckBody
              isLoading={isLoading}
              title={title}
              onTitleChange={setTitle}
              cards={cards}
              isCardNew={isCardNew}
              isCardEdited={isCardEdited}
              onFieldChange={updateCardField}
              onRemove={removeCard}
              onAddBelow={addCardBelow}
            />
          </Box>
        </>
      )}

      {!isLoading && !loadError && (
        <>
          <Loader id="editDeck__loader_saving" isOpen={isSaving} />

          <MssModal
            isOpen={isPromptOpen}
            onClose={stay}
            title="Unsaved Changes"
            id="editDeck__modal_unsavedChanges"
            primaryButtonTitle="Stay"
            onPrimaryClick={stay}
            secondaryButtonTitle="Leave"
            onSecondaryClick={leave}
          >
            <Typography>Unsaved edits will not be applied. Do you wish to leave?</Typography>
          </MssModal>
        </>
      )}
    </Box>
  );
}

export default EditDeck;
